/**
 * "Chybějící doklady": which outgoing payments still owe a document, and
 * which of them look like they never need one. Pure, so vitest covers it
 * (./__tests__/rules.test.ts); the queries are in src/hooks/useMissingDocuments.ts.
 *
 * The rule matches the weekly digest (supabase/functions/_shared/weekly-digest.ts)
 * and the Transactions page: money out, not matched, status empty or "missing".
 * "Nepotřebné" is status `not_needed` with `matched = true`, the same pair the
 * status chip (MatchBadge) writes. So AI matching, which reads only
 * `matched = false`, never touches it, and the match rate counts it as settled.
 */

export interface MissingDocTransaction {
  id: string;
  date: string | null;
  amount: number | null;
  currency: string | null;
  counter_name: string | null;
  counter_account: string | null;
  counter_bank_code: string | null;
  description: string | null;
  category: string | null;
  status: string | null;
  matched: boolean | null;
  receipt_id?: string | null;
  invoice_id?: string | null;
}

/** An outgoing payment that still owes a document. */
export function needsDocument(
  t: Pick<MissingDocTransaction, "amount" | "matched" | "status">
): boolean {
  if (t.amount === null || !(t.amount < 0)) return false;
  if (t.matched) return false;
  return t.status === null || t.status === "missing";
}

/** Marked "Nepotřebné" (reversible from the page's second tab). */
export function isNotNeeded(t: Pick<MissingDocTransaction, "amount" | "status">): boolean {
  return t.status === "not_needed" && t.amount !== null && t.amount < 0;
}

/**
 * Why a payment probably needs no document:
 * - `fee`: Fio's own movement type "Poplatek" (stored as category "Fees");
 * - `tax`: paid to an account held at the Czech National Bank (bank code 0710),
 *   where the tax offices, the social security administration and the health
 *   insurers keep their accounts;
 * - `own_transfer`: the same amount arrives in the company the same day or the
 *   next (a transfer between two of its own accounts). bank_accounts has no
 *   account numbers to compare with, so the mirror image is the signal.
 */
export type NotNeededReason = "fee" | "tax" | "own_transfer";

export const CNB_BANK_CODE = "0710";

/** Incoming rows the own-transfer check compares against. */
export interface IncomingRow {
  id: string;
  date: string | null;
  amount: number | null;
  currency: string | null;
}

const DAY_MS = 86_400_000;

function dayNumber(iso: string | null): number | null {
  if (!iso) return null;
  const t = Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isFinite(t) ? Math.round(t / DAY_MS) : null;
}

/** Index of incoming payments by currency and amount in cents, for the mirror check. */
export function incomingIndex(rows: readonly IncomingRow[]): Map<string, number[]> {
  const index = new Map<string, number[]>();
  for (const r of rows) {
    if (r.amount === null || !(r.amount > 0)) continue;
    const day = dayNumber(r.date);
    if (day === null) continue;
    const key = `${r.currency || "CZK"}:${Math.round(r.amount * 100)}`;
    const list = index.get(key) ?? [];
    list.push(day);
    index.set(key, list);
  }
  return index;
}

export function notNeededReason(
  t: MissingDocTransaction,
  incoming: Map<string, number[]>
): NotNeededReason | null {
  if (t.category === "Fees") return "fee";
  if ((t.counter_bank_code ?? "").trim() === CNB_BANK_CODE) return "tax";
  if (t.amount !== null && t.amount < 0) {
    const day = dayNumber(t.date);
    const days = incoming.get(`${t.currency || "CZK"}:${Math.round(-t.amount * 100)}`);
    if (day !== null && days?.some((d) => d - day >= 0 && d - day <= 1)) return "own_transfer";
  }
  return null;
}

/**
 * Who was paid: the counterparty, else the payment's text, else the
 * counter-account ("2900123456/2010") for a bare transfer with neither.
 */
export function payee(
  t: Pick<MissingDocTransaction, "counter_name" | "description"> &
    Partial<Pick<MissingDocTransaction, "counter_account" | "counter_bank_code">>
): string {
  const text = (t.counter_name || t.description || "").replace(/\s+/g, " ").trim();
  if (text) return text;
  const account = (t.counter_account || "").trim();
  if (!account) return "";
  const bank = (t.counter_bank_code || "").trim();
  return bank ? `${account}/${bank}` : account;
}

/** Money out per currency, as positive sums. */
export function sumsByCurrency(
  txs: readonly Pick<MissingDocTransaction, "amount" | "currency">[]
): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of txs) {
    const currency = t.currency || "CZK";
    out[currency] = (out[currency] ?? 0) + Math.abs(t.amount ?? 0);
  }
  return out;
}

/** The status and flag a row gets when marked "Nepotřebné", and back. */
export function notNeededUpdate(markNotNeeded: boolean, t: MissingDocTransaction) {
  if (markNotNeeded) return { status: "not_needed" as const, matched: true };
  return { status: null, matched: !!(t.receipt_id || t.invoice_id) };
}

/** The windows the page offers. */
export type MissingDocsPeriod = "90d" | "year" | "all";

export function periodStart(period: MissingDocsPeriod, today: Date = new Date()): string | null {
  if (period === "all") return null;
  if (period === "year") return `${today.getFullYear()}-01-01`;
  const d = new Date(today.getTime() - 90 * DAY_MS);
  return d.toISOString().slice(0, 10);
}

/** The slice of a receipt the attach dialog ranks. */
export interface RankableReceipt {
  id: string;
  amount: number | null;
  currency: string | null;
  issue_date: string | null;
  vendor: string | null;
  file_name: string | null;
}

/**
 * Unmatched receipts for one payment, likeliest first: the same amount and
 * currency, then the closest amount, then the closest date. `query` narrows by
 * vendor or file name.
 */
export function rankReceipts<R extends RankableReceipt>(
  t: Pick<MissingDocTransaction, "amount" | "currency" | "date">,
  receipts: readonly R[],
  query = ""
): R[] {
  const target = Math.abs(t.amount ?? 0);
  const day = dayNumber(t.date);
  const needle = query.trim().toLowerCase();
  const score = (r: R) => {
    const sameCurrency = (r.currency || "CZK") === (t.currency || "CZK");
    const diff = Math.abs(Math.abs(r.amount ?? 0) - target);
    const exact = sameCurrency && diff < 0.005;
    const rDay = dayNumber(r.issue_date);
    const dateGap = day !== null && rDay !== null ? Math.abs(rDay - day) : 365;
    return { exact, diff: sameCurrency ? diff : Number.POSITIVE_INFINITY, dateGap };
  };
  return receipts
    .filter(
      (r) =>
        !needle ||
        (r.vendor ?? "").toLowerCase().includes(needle) ||
        (r.file_name ?? "").toLowerCase().includes(needle)
    )
    .map((r) => ({ r, s: score(r) }))
    .sort(
      (a, b) =>
        Number(b.s.exact) - Number(a.s.exact) || a.s.diff - b.s.diff || a.s.dateGap - b.s.dateGap
    )
    .map(({ r }) => r);
}

/** A row amount: Czech formatting, two decimals, the sign as stored. */
export function formatAmount(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat("cs-CZ", {
      style: "currency",
      currency: currency || "CZK",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || "CZK"}`;
  }
}
